import { SupabaseClient, User } from "@supabase/supabase-js";
import { Users } from "../users/types";
import { readProvedLink } from "@/lib/auth/linked-identity";

/** The link row with its person embedded, as {@link getCurrentUser} selects it. */
interface LinkedUserRow {
  users: Users | null;
}

/**
 * Gets the current user from the database.
 *
 * Resolves the person through `nowadays.user_auth_identity`, keyed on the login's
 * id, and never through the session's email claim. Anybody can create a login on
 * somebody else's address, so the claim does not identify a person (ENG-726). A
 * signup link nobody has proved yet does not count either.
 *
 * @param supabase - The Supabase client. A session client reads only its own
 *   link under RLS. A service-role client must be given `authUser`.
 * @param authUser - Optional pre-resolved auth user. Pass it to skip the
 *   `auth.getUser()` roundtrip when the session has already been fetched this
 *   request (e.g. via `getCachedAuthUser`). `undefined` means "resolve it here";
 *   `null` means "no session". When omitted, behavior is unchanged.
 * @returns The current user, or `null` when there is no session.
 * @throws An error if a session exists but no person is linked to its login.
 */
export async function getCurrentUser(supabase: SupabaseClient, authUser?: User | null): Promise<Users | null> {
  let sessionUser = authUser;
  if (sessionUser === undefined) {
    const { data, error } = await supabase.auth.getUser();
    if (error) {
      return null;
    }
    sessionUser = data.user;
  }

  if (!sessionUser?.email) {
    return null;
  }

  const link = await readProvedLink<LinkedUserRow>(supabase, sessionUser.id, "users(*)").catch((error: Error) => {
    throw new Error(`User not found in database: ${error.message}`);
  });
  if (!link?.users) {
    throw new Error("User not found in database: login is not linked to a user");
  }

  return link.users;
}

/**
 * Display names for a set of `nowadays.users` IDs, falling back to email when a user has no
 * first/last name on file. Shared by every caller that needs to label a raw `user_id` for display
 * (e.g. {@link getEventOwnerNames}, the admin Venues tab's "Added by"/"RFP sent by" columns), so
 * the name-or-email fallback lives in one place instead of being re-copied per caller.
 *
 * @param supabase - The Supabase client.
 * @param userIds - The user IDs to resolve; deduped internally, empty input short-circuits.
 * @returns Map of user_id to display name; a user with neither a name nor an email is omitted.
 * @throws Never - a failed read logs and returns an empty map.
 */
export async function getUserNamesByIds(supabase: SupabaseClient, userIds: number[]): Promise<Map<number, string>> {
  const names = new Map<number, string>();
  const uniqueIds = [...new Set(userIds)];
  if (uniqueIds.length === 0) return names;

  const { data: users, error } = await supabase
    .schema("nowadays")
    .from("users")
    .select("user_id, first_name, last_name, email")
    .in("user_id", uniqueIds);

  if (error || !users) {
    console.error("getUserNamesByIds: failed to read users", error);
    return names;
  }

  for (const u of users) {
    // filter(Boolean) drops empty name parts, so the join needs no trim; fall back to email.
    const display = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;
    if (display) names.set(u.user_id, display);
  }
  return names;
}

/**
 * Batched client-owner display names for many events — the passed-dates Slack digest resolves the
 * "event owner" (the client contact in `user_event_access` with `access_type = 'owner'`) for its
 * whole list in two queries instead of per event. Distinct from the internal POC (the claimed admin
 * the digest actually pings). Best-effort: on any error it logs and returns an empty map so a
 * display-only caller degrades to "no owner" rather than failing the whole notification.
 *
 * @param supabase - The Supabase client (service role in the cron path).
 * @param eventIds - The events to resolve; deduped internally, empty input short-circuits.
 * @returns Map of event_id → owner display name (full name, else email); events with no owner are omitted.
 */
export async function getEventOwnerNames(supabase: SupabaseClient, eventIds: number[]): Promise<Map<number, string>> {
  const names = new Map<number, string>();
  const uniqueIds = [...new Set(eventIds)];
  if (uniqueIds.length === 0) return names;

  const { data: access, error: accessError } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("event_id, user_id")
    .in("event_id", uniqueIds)
    .eq("access_type", "owner");

  if (accessError || !access) {
    console.error("getEventOwnerNames: failed to read user_event_access", accessError);
    return names;
  }

  const nameByUser = await getUserNamesByIds(
    supabase,
    access.map((a) => a.user_id)
  );

  // First owner wins per event (an event rarely has more than one owner-access row).
  for (const a of access) {
    const display = nameByUser.get(a.user_id);
    if (display && !names.has(a.event_id)) names.set(a.event_id, display);
  }
  return names;
}
